import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Clock, Users, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { activityService } from '@/lib/api/activity.service';
import type { CreateActivityDto, ActivityStatus, ActivityWithAssociations } from '@/types/activity.types';

interface SubActivityFormProps {
  parentActivityId: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const SubActivityForm: React.FC<SubActivityFormProps> = ({ 
  parentActivityId, 
  onSuccess,
  onCancel 
}) => {
  const navigate = useNavigate();
    const { t } = useTranslation(['activities', 'common']);
  const [loading, setLoading] = useState(false);
  const [loadingParent, setLoadingParent] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [parentActivity, setParentActivity] = useState<ActivityWithAssociations | null>(null);
  const [useCustomTime, setUseCustomTime] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: '', // HH:MM format
    endTime: '',   // HH:MM format
    location: '',
    status: 'draft' as ActivityStatus,
  });

  // Load parent activity details
  useEffect(() => {
    const loadParentActivity = async () => {
      setLoadingParent(true);
      try {
        const data = await activityService.getActivity(parentActivityId);
        setParentActivity(data);
        
        // Set default location and times from parent
        if (data.start_time) {
          const startDate = new Date(data.start_time);
          const endDate = data.end_time ? new Date(data.end_time) : null;
          
          setFormData(prev => ({
            ...prev,
            location: data.location || '',
            startTime: `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`,
            endTime: endDate ? `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}` : ''
          }));
        }
      } catch (err) {
        console.error('Error loading parent activity:', err);
        setError(t('activities:subActivity.errors.loadParent'));
      } finally {
        setLoadingParent(false);
      }
    };

    loadParentActivity();
  }, [parentActivityId]);

  // Get parent date for combining with time
  const getParentDate = (): Date | null => {
    if (!parentActivity?.start_time) return null;
    return new Date(parentActivity.start_time);
  };

  // Convert time string to full datetime
  const timeToDateTime = (timeStr: string): string | undefined => {
    if (!timeStr || !useCustomTime) return undefined;
    const parentDate = getParentDate();
    if (!parentDate) return undefined;
    
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date(parentDate);
    date.setHours(hours, minutes, 0, 0);
    return date.toISOString();
  };

  // Get min/max times from parent
  const getParentStartTime = (): string => {
    if (!parentActivity?.start_time) return '00:00';
    const date = new Date(parentActivity.start_time);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  const getParentEndTime = (): string => {
    if (!parentActivity?.end_time) return '23:59';
    const date = new Date(parentActivity.end_time);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  // Validate time is within parent range
  const isTimeValid = (time: string, isEnd: boolean = false): boolean => {
    if (!time) return true;
    const parentStart = getParentStartTime();
    const parentEnd = getParentEndTime();
    
    if (time < parentStart) return false;
    if (time > parentEnd) return false;
    
    if (isEnd && formData.startTime && time <= formData.startTime) return false;
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title) {
      setError(t('activities:subActivity.errors.titleRequired'));
      return;
    }

    if (!parentActivity) {
      setError(t('activities:subActivity.errors.parentNotLoaded'));
      return;
    }

    // Validate times if custom time is enabled
    if (useCustomTime) {
      if (!isTimeValid(formData.startTime)) {
        setError(t('activities:subActivity.errors.startTimeRange', { start: formatTime(getParentStartTime()), end: formatTime(getParentEndTime()) }));
        return;
      }
      if (!isTimeValid(formData.endTime, true)) {
        setError(t('activities:subActivity.errors.endTimeRange', { end: formatTime(getParentEndTime()) }));
        return;
      }
    }
    
    setLoading(true);
    setError(null);

    try {
      const submitData: Omit<CreateActivityDto, 'event_id'> = {
        title: formData.title,
        description: formData.description || undefined,
        start_time: timeToDateTime(formData.startTime),
        end_time: timeToDateTime(formData.endTime),
        location: formData.location || undefined,
        status: formData.status,
      };

      const subActivity = await activityService.createSubActivity(parentActivityId, submitData);
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`/activities/${subActivity.id}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('activities:subActivity.errors.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(`/activities/${parentActivityId}`);
    }
  };

  const formatTime = (time: string): string => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loadingParent) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-muted-foreground">{t('activities:subActivity.loading')}</span>
      </div>
    );
  }

  if (!parentActivity) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
        {t('activities:subActivity.errors.loadParent')}
      </div>
    );
  }

  const parentStartTime = getParentStartTime();
  const parentEndTime = getParentEndTime();

  return (
    <div className="max-w-xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={handleCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-lg font-bold">{t('activities:subActivity.titles.new')}</h1>
          <p className="text-xs text-muted-foreground">
            {t('activities:subActivity.labels.forParent', { title: parentActivity.title })}
          </p>
        </div>
      </div>

      {/* Parent Timeline Info */}
      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="h-4 w-4 text-purple-600" />
          <span className="text-sm font-semibold text-purple-900">{t('activities:subActivity.sections.parentTimeline')}</span>
        </div>
        <div className="text-sm text-purple-800">
          <span className="font-medium">{parentActivity.start_time ? formatDateTime(parentActivity.start_time) : 'Not set'}</span>
          {parentActivity.end_time && (
            <span> → {formatDateTime(parentActivity.end_time)}</span>
          )}
        </div>
        <p className="text-xs text-purple-600 mt-1">
          {t('activities:subActivity.helpers.timeRange')}
        </p>
      </div>

      {/* Inherited Leads */}
      {parentActivity.leads && parentActivity.leads.length > 0 && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-900">{t('activities:subActivity.sections.inheritedLeads')}</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {parentActivity.leads.map((lead) => (
              <Badge key={lead.id} variant="outline" size="sm" className="bg-white">
                {lead.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-4 space-y-4">
        {/* Title */}
        <div>
          <Label htmlFor="title" className="text-xs font-medium">{t('activities:subActivity.fields.title')} *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder={t('activities:subActivity.placeholders.activityTitle')}
            required
            className="mt-1"
          />
        </div>

        {/* Time Selection */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="useCustomTime"
              checked={useCustomTime}
              onChange={(e) => setUseCustomTime(e.target.checked)}
              className="rounded text-purple-600"
            />
            <Label htmlFor="useCustomTime" className="text-xs font-medium cursor-pointer">
              {t('activities:subActivity.options.customTime')}
            </Label>
          </div>

          {useCustomTime && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-xs text-muted-foreground">
                  Start Time ({formatTime(parentStartTime)} - {formatTime(parentEndTime)})
                </Label>
                <Input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                  min={parentStartTime}
                  max={parentEndTime}
                  className={`mt-1 ${!isTimeValid(formData.startTime) ? 'border-red-300' : ''}`}
                  compact
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">
                  End Time (after start, before {formatTime(parentEndTime)})
                </Label>
                <Input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                  min={formData.startTime || parentStartTime}
                  max={parentEndTime}
                  className={`mt-1 ${!isTimeValid(formData.endTime, true) ? 'border-red-300' : ''}`}
                  compact
                />
              </div>
            </div>
          )}
        </div>

        {/* Location & Status */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="location" className="text-xs font-medium">Location</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              placeholder={parentActivity.location || 'Same as parent'}
              className="mt-1"
              compact
            />
          </div>
          <div>
            <Label htmlFor="status" className="text-xs font-medium">Status</Label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as ActivityStatus }))}
              className="mt-1 w-full h-8 px-2 text-sm rounded-md border border-input bg-background"
            >
              <option value="draft">Draft</option>
              <option value="planned">Planned</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="description" className="text-xs font-medium">Description</Label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Brief description..."
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:ring-2 focus:ring-ring resize-none"
            rows={2}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={handleCancel} disabled={loading}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" size="sm" disabled={loading}>
            {loading && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2" />}
            <Save className="h-3 w-3 mr-1" />
            Create
          </Button>
        </div>
      </form>
    </div>
  );
};
